import { forwardRef, type HTMLAttributes } from 'react'
import { cn } from '@/lib/utils'
import { ROLE_TO_VARIANT, type StatusRole } from '@/design-system/tokens/status'

/** Варианты точки статуса (`.dot`/`.d-*` макета). Серый = третичный текст. */
export type DotVariant = 'red' | 'amber' | 'green' | 'gray'

const DOT_COLOR: Record<DotVariant, string> = {
  red: 'bg-red',
  amber: 'bg-amber',
  green: 'bg-green',
  gray: 'bg-txt-tertiary', // серый «нет-данных» = text-tertiary
}

export interface StatusDotProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: DotVariant
  /** Семантическая роль (danger/warning/success/neutral) — приоритетнее variant. */
  status?: Exclude<StatusRole, 'info'>
  /** Доступное имя — статус не должен передаваться только цветом. */
  label: string
}

export const StatusDot = forwardRef<HTMLSpanElement, StatusDotProps>(
  ({ className, variant, status, label, ...props }, ref) => {
    const resolved = (status ? ROLE_TO_VARIANT[status] : (variant ?? 'gray')) as DotVariant
    return (
      <span ref={ref} className="inline-flex items-center gap-1.5" {...props}>
        <span
          aria-hidden
          className={cn('inline-block size-dot shrink-0 rounded-round', DOT_COLOR[resolved], className)}
        />
        <span className="sr-only">{label}</span>
      </span>
    )
  },
)
StatusDot.displayName = 'StatusDot'
